import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoriesSection from './components/CategoriesSection';
import HowItWorksSection from './components/HowItWorksSection';
import FAQSection from './components/FAQSection';
import { articles, comparisonData, faqData } from '@/mocks/articles';

export default function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.skin';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'VigorBuy Spreadsheet 2026 | Ultimate Buying Resource',
      description: 'Access the latest VigorBuy Spreadsheet with 10000+ verified product links across shoes, clothing, hoodies, bags, accessories and more.',
      url: `${siteUrl}/`,
      mainEntity: {
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latestArticles = articles.slice(0, 9);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <HeroSection />

        {/* Stats / Trust Section */}
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary-500 mb-3">
                Trusted by Shoppers
              </p>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                The VigorBuy Spreadsheet helps buyers find trusted products, compare prices, review QC photos,
                and simplify the purchasing process. Our independent resource center aggregates the most reliable
                VigorBuy spreadsheet data available anywhere online.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {[
                { value: '10,000+', label: 'Products Listed', icon: 'ri-stack-line' },
                { value: '200+', label: 'Categories Covered', icon: 'ri-grid-line' },
                { value: 'Daily', label: 'Updated & Verified', icon: 'ri-refresh-line' },
                { value: 'Global', label: 'Shipping Support', icon: 'ri-global-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-lg`} />
                  </div>
                  <p className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why VigorBuy Section */}
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-secondary-500 mb-3">
                  Why VigorBuy Spreadsheet
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 leading-tight tracking-tight mb-6">
                  The Smartest Way to Shop with VigorBuy
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  Shopping through VigorBuy without a reliable spreadsheet means wasting hours searching
                  for trustworthy product links. Our spreadsheet eliminates guesswork by providing daily
                  verified links, QC photo previews, and direct access to the best products across every
                  category. Whether you are hunting for the latest sneaker releases, building a streetwear
                  collection, or looking for everyday essentials, the VigorBuy Spreadsheet keeps you ahead
                  with fresh, accurate data every single day.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { before: 'Too many fake or dead links', after: 'Verified spreadsheet updated daily' },
                    { before: 'Confusing QC and shipping process', after: 'Clear guides and tutorials' },
                    { before: 'Hours wasted searching manually', after: 'Instant access to 10000+ products' },
                    { before: 'No way to compare options', after: 'Price and quality at a glance' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <p className="text-xs text-foreground-400 line-through mb-1">{item.before}</p>
                      <p className="text-sm font-semibold text-accent-700">{item.after}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20flat%20lay%20of%20premium%20sneakers%20hoodie%20and%20accessories%20on%20light%20warm%20beige%20surface%20with%20soft%20natural%20morning%20light%20streaming%20from%20right%2C%20lifestyle%20product%20photography%2C%20warm%20neutral%20tones%2C%20editorial%20aesthetic%2C%20modern%20clean%20composition%20with%20scattered%20fabric%20swatches&width=700&height=600&seq=why-vigorbuy-v3&orientation=portrait"
                  alt="Why Use VigorBuy Spreadsheet for Smart Shopping"
                  title="VigorBuy Spreadsheet Shopping Benefits"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <CategoriesSection />

        <HowItWorksSection />

        {/* Is VigorBuy Safe Section */}
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="rounded-2xl overflow-hidden border border-background-200/60 order-2 lg:order-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Conceptual%20security%20and%20trust%20theme%20with%20shopping%20bags%20and%20protective%20shield%20icon%20on%20warm%20cream%20surface%2C%20soft%20natural%20afternoon%20lighting%2C%20minimalist%20product%20photography%2C%20warm%20neutral%20tones%2C%20clean%20contemporary%20composition%20with%20gentle%20highlights&width=700&height=600&seq=safe-shopping-v3&orientation=portrait"
                  alt="Is VigorBuy Safe - Shopping Security Guide"
                  title="VigorBuy Safe Shopping Information"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="order-1 lg:order-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-secondary-500 mb-3">
                  Safety &amp; Trust
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 leading-tight tracking-tight mb-6">
                  Is VigorBuy Safe? Yes — Here is Why
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  VigorBuy provides secure shopping services and product sourcing assistance with buyer protection
                  and transparent processes. The platform includes detailed QC photo verification, multiple shipping
                  options with real-time tracking, and responsive customer support. Our independent guides help
                  users understand potential risks, navigate customs requirements for different countries, select
                  appropriate shipping lines based on package weight and destination, and make fully informed
                  purchasing decisions with confidence.
                </p>
                <ul className="space-y-3">
                  {[
                    'QC photo verification before international shipping',
                    'Multiple secure payment methods with buyer protection',
                    'Real-time package tracking across all shipping lines',
                    'Independent guides reviewed by experienced shoppers',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-accent-500">
                        <i className="ri-shield-check-line" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-10 md:mb-14">
              <p className="text-xs font-semibold uppercase tracking-widest text-secondary-500 mb-3">
                Honest Comparison
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight tracking-tight">
                VigorBuy vs<br />Other Agents
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {comparisonData.map((item) => (
                <div
                  key={item.brand}
                  className={`rounded-xl border bg-background-50 overflow-hidden ${
                    item.highlight
                      ? 'border-primary-400 ring-1 ring-primary-300'
                      : 'border-background-200/70'
                  }`}
                >
                  <div className={`px-5 py-4 ${item.highlight ? 'bg-primary-500' : 'bg-background-100'}`}>
                    <h3 className={`text-lg font-bold ${item.highlight ? 'text-background-50' : 'text-foreground-950'}`}>
                      {item.brand}
                    </h3>
                  </div>
                  <div className="p-5 space-y-3">
                    {item.features.map((feat) => (
                      <div key={feat.name} className="flex items-center justify-between">
                        <span className="text-xs md:text-sm text-foreground-600">{feat.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-foreground-500">{feat.value}</span>
                          <span className={`w-4 h-4 flex items-center justify-center ${
                            feat.has ? 'text-accent-500' : 'text-foreground-300'
                          }`}>
                            <i className={feat.has ? 'ri-check-line' : 'ri-close-line'} />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Preview Section - 9 Cards */}
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent-300 bg-accent-100/60 text-xs font-semibold text-accent-800 uppercase tracking-wide mb-4">
                Resource Center
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight tracking-tight">
                Latest VigorBuy<br />Spreadsheet Updates
              </h2>
              <p className="mt-4 text-sm text-foreground-600 max-w-lg mx-auto">
                In-depth guides, tips, and reviews to help you master the VigorBuy shopping experience.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <a
                  key={article.id}
                  href={`/article/${article.slug}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/article/${article.slug}`); window.scrollTo(0, 0); }}
                  className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-background-100">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                        {article.tag}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-3">
                      {article.excerpt}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center mt-10">
              <a
                href="/article/vigorbuy-spreadsheet-guide"
                onClick={(e) => { e.preventDefault(); navigate('/article/vigorbuy-spreadsheet-guide'); window.scrollTo(0, 0); }}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold border-2 border-foreground-800 text-foreground-800 rounded-full hover:bg-foreground-800 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Read All VigorBuy Guides
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-28 px-6 md:px-10 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20flat%20lay%20of%20diverse%20fashion%20items%20sneakers%20clothing%20accessories%20bags%20artfully%20scattered%20across%20warm%20cream%20surface%20with%20soft%20natural%20morning%20light%2C%20editorial%20product%20collage%20photography%2C%20warm%20neutral%20toned%20composition%2C%20clean%20modern%20aesthetic%20with%20varied%20textures&width=1600&height=800&seq=cta-bg-collage-v3&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-foreground-950/75" />
          </div>

          <div className="relative z-10 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
              <div>
                <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight tracking-tight">
                  Ready to buy<br />like a pro?
                </h2>
              </div>
              <div className="flex flex-col items-start gap-5">
                <p className="text-sm md:text-base text-background-50/80 max-w-md leading-relaxed">
                  Access verified product links, QC guides, and shipping tutorials trusted by thousands
                  of smart shoppers. Start exploring the VigorBuy Spreadsheet now.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-background-50 text-foreground-950 rounded-full hover:bg-background-50/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <span className="w-5 h-5 flex items-center justify-center bg-foreground-950 text-background-50 rounded-full">
                    <i className="ri-arrow-right-line text-xs" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <FAQSection />
      </main>

      <Footer />
    </div>
  );
}